import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Icon from "@/components/Icon";
import { Input } from "@/components/ui/input";
import { Search, Store } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useHeatmap } from "@/hooks/useHeatmap";

export default function CategoryPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const city = params.get("city") || "";
  const [cats, setCats] = useState([]);
  const [query, setQuery] = useState("");
  const { heatFor } = useHeatmap();

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const qs = new URLSearchParams();
    if (city) qs.set("city", city);
    if (query) qs.set("search", query);
    navigate(`/vendors?${qs.toString()}`);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Choose a category</div>
        <div className="flex items-baseline gap-3">
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter">
            {city ? `Businesses in ${city}` : "Browse all categories"}
          </h1>
          {city && (
            <Link
              to="/cities"
              className="text-sm text-primary underline"
              data-testid="change-city-link"
            >
              Change city
            </Link>
          )}
        </div>
        <p className="text-muted-foreground mt-2">28 categories covering everything our community needs.</p>
      </div>

      <form onSubmit={onSearch} className="mb-8 max-w-2xl">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="category-search-input"
            className="pl-9 h-12 rounded-full"
            placeholder="Search business, service, product, vendor, city…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </form>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4 stagger">
        {cats.map((c) => {
          const qs = new URLSearchParams();
          qs.set("category", c.name);
          if (city) qs.set("city", city);
          const heat = heatFor("category", c.name, c.color);
          return (
            <Link
              key={c.slug}
              to={`/vendors?${qs.toString()}`}
              data-testid={`category-link-${c.slug}`}
              className="group rounded-2xl border border-border/60 bg-card p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg flex flex-col items-center text-center"
            >
              <div
                className="h-14 w-14 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110"
                style={{ background: heat.bg, color: heat.fg }}
                title={heat.count > 0 ? `${heat.vendors} vendor${heat.vendors === 1 ? "" : "s"}` : "No vendors yet"}
              >
                <Icon name={c.icon} className="h-7 w-7" />
              </div>
              <div className="mt-3 text-xs sm:text-sm font-medium leading-tight">{c.name}</div>
              {heat.count > 0 && (
                <div className="mt-1 text-[10px] text-muted-foreground inline-flex items-center gap-0.5" data-testid={`category-heat-${c.slug}`}>
                  <Store className="h-3 w-3" /> {heat.vendors}
                </div>
              )}
            </Link>
          );
        })}
      </div>

      {city && (
        <div className="mt-10">
          <Badge variant="secondary">Tip</Badge>
          <p className="text-sm text-muted-foreground mt-2">
            You can filter, sort and choose page sizes (50/75/100/150) on the vendor listing.
          </p>
        </div>
      )}
    </div>
  );
}
